import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { action, subscribe } from '../lib/api.ts';
import { dateTime, moneyIn, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { Kpi, Table } from './Reports.tsx';

/**
 * Card payments: payment links sent to customers, what came in, refunds back to the card, and
 * the payment providers the business has connected. ERP PRO never sees a card number: the card
 * is typed on the provider's own page.
 */

type State = 'open' | 'processing' | 'succeeded' | 'cancelled' | 'expired';
const STATE: Record<State, { tone: Tone; icon: IconName }> = {
  open: { tone: 'info', icon: 'clock' },
  processing: { tone: 'warn', icon: 'repeat' },
  succeeded: { tone: 'ok', icon: 'check' },
  cancelled: { tone: 'neutral', icon: 'x' },
  expired: { tone: 'neutral', icon: 'clock' },
};
const stateLabel = (s: State) => t(`pm.st_${s}` as MessageKey);

interface Overview {
  currency: string;
  totals: { currency: string; received: number; paid: number; waiting: number; open: number; refunded: number }[];
  refunds_pending: number;
  refunds_failed: number;
  ready: { account: string; provider: string; test: boolean } | null;
  accounts: number;
  site_url: string | null;
  can_seal: boolean;
}
interface Row {
  id: string;
  ref: string;
  amount: number;
  refunded: number;
  currency: string;
  description: string;
  label: string | null;
  module: string | null;
  name: string | null;
  email: string | null;
  state: State;
  method: string | null;
  error: string | null;
  at: string;
  paid_at: string | null;
  account_name: string;
  provider: string;
  refund_pending: boolean;
}
interface RefundRow {
  id: string;
  amount: number;
  reason: string;
  state: 'pending' | 'succeeded' | 'failed';
  error: string | null;
  by_name: string | null;
  at: string;
  done_at: string | null;
}
interface Detail extends Row {
  path: string;
  url: string | null;
  expires_at: string;
  test: boolean;
  created_by_name: string | null;
  closed_by_name: string | null;
  return_path: string | null;
  refunds: RefundRow[];
  refundable: number;
}
interface Account {
  id: string;
  name: string;
  provider: 'simulator' | 'stripe';
  active: boolean;
  is_default: boolean;
  live: boolean;
  checked_at: string | null;
  checked_name: string | null;
  last_error: string | null;
  has_secret: boolean;
  secret_readable: boolean;
  has_webhook: boolean;
  webhook_url: string | null;
  problems: string[];
  payments: number;
}
interface SettingsData {
  site_url: string | null;
  link_days: number;
  can_seal: boolean;
  accounts: Account[];
}

/** The full web address of a link: the one set in Settings, else this browser's. */
const fullUrl = (d: { url: string | null; path: string }) => d.url ?? `${window.location.origin}${d.path}`;
const forLabel = (r: { label: string | null; module: string | null }) => r.label ?? (r.module ? t(`pm.for_${r.module}` as MessageKey) : t('pm.for_link'));

type Tab = 'payments' | 'settings';
const FILTERS = ['all', 'open', 'paid', 'refunded', 'closed'] as const;
type Filter = (typeof FILTERS)[number];

export function Payments() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const manage = can('payments.manage');
  const { data: o, reload } = useAction<Overview>('payments.overview');
  useEffect(() => subscribe(['payments'], () => void reload()), [reload]);
  const tab: Tab = manage && params.get('tab') === 'settings' ? 'settings' : 'payments';
  const setTab = (k: Tab) => setParams(k === 'payments' ? {} : { tab: k }, { replace: true });
  const [making, setMaking] = useState(false);
  const base = o?.totals.find((x) => x.currency === o.currency) ?? o?.totals[0] ?? null;
  const cur = base?.currency ?? o?.currency ?? null;
  return (
    <Shell>
      <div className="page reports pm-page">
        <div className="page-head">
          <div>
            <h1>{t('pm.title')}</h1>
            <p className="muted">{!o ? ' ' : o.ready ? t('pm.lead_ready', { account: o.ready.account }) : t('pm.lead_not_ready')}</p>
          </div>
          {o?.ready && can('payments.links') && tab === 'payments' ? (
            <Button variant="primary" icon="plus" onClick={() => setMaking(true)}>
              {t('pm.new_link')}
            </Button>
          ) : null}
        </div>
        {o?.ready?.test ? (
          <p className="callout callout-warn pm-test" role="note">
            <Icon name="alert" size={16} />
            <span>{o.ready.provider === 'simulator' ? t('pm.test_simulator') : t('pm.test_keys')}</span>
          </p>
        ) : null}
        {o && !o.ready ? (
          <div className="callout callout-info pm-setup">
            <Icon name="card" size={20} />
            <div>
              <p>
                <strong>{t('pm.setup_title')}</strong>
              </p>
              <p className="muted">{t('pm.setup_body')}</p>
              {manage ? (
                <Button size="sm" icon="settings" onClick={() => setTab('settings')}>
                  {t('pm.setup_go')}
                </Button>
              ) : (
                <p className="small muted">{t('pm.setup_ask')}</p>
              )}
            </div>
          </div>
        ) : null}
        {!o ? (
          <Skeleton h={110} r="var(--radius-lg)" />
        ) : (
          <section className="rep-kpis" aria-label={t('pm.at_a_glance')}>
            <Kpi label={t('pm.k_received')} value={cur ? moneyIn(base?.received ?? 0, cur) : '—'} hint={tn('pm.k_payments', base?.paid ?? 0)} />
            <Kpi label={t('pm.k_waiting')} value={cur ? moneyIn(base?.waiting ?? 0, cur) : '—'} hint={tn('pm.k_links', base?.open ?? 0)} />
            <Kpi label={t('pm.k_refunded')} value={cur ? moneyIn(base?.refunded ?? 0, cur) : '—'} hint={t('pm.k_30_days')} />
            <Kpi
              label={t('pm.k_refunds')}
              value={String(o.refunds_pending)}
              hint={o.refunds_failed ? tn('pm.k_failed', o.refunds_failed) : t('pm.k_refunds_hint')}
              warn={o.refunds_failed > 0}
            />
          </section>
        )}
        {o && o.totals.length > 1 ? (
          <p className="muted small pm-other">
            {o.totals
              .filter((x) => x !== base)
              .map((x) => t('pm.other_currency', { amount: moneyIn(x.received, x.currency), n: x.paid }))
              .join(' · ')}
          </p>
        ) : null}
        {manage ? (
          <div className="segmented tabs" role="tablist" aria-label={t('pm.sections')}>
            {(['payments', 'settings'] as const).map((k) => (
              <button key={k} role="tab" aria-selected={tab === k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>
                <Icon name={k === 'payments' ? 'card' : 'settings'} size={16} />
                {t(`pm.tab_${k}` as MessageKey)}
              </button>
            ))}
          </div>
        ) : null}
        {!o ? <Skeleton h={320} /> : tab === 'payments' ? <PaymentsTab ready={!!o.ready} onNew={() => setMaking(true)} /> : <SettingsTab onChange={reload} />}
        {making && o ? <NewLink currency={o.currency} onClose={() => setMaking(false)} onDone={reload} /> : null}
      </div>
    </Shell>
  );
}

function PaymentsTab({ ready, onNew }: { ready: boolean; onNew: () => void }) {
  const navigate = useNavigate();
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const filter = (FILTERS.includes(params.get('f') as Filter) ? params.get('f') : 'all') as Filter;
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  useEffect(() => {
    const id = window.setTimeout(() => setQuery(q.trim()), 250);
    return () => window.clearTimeout(id);
  }, [q]);
  const { data: rows, reload } = useAction<Row[]>('payments.list', { filter, ...(query ? { q: query } : {}) });
  useEffect(() => subscribe(['payments'], () => void reload()), [reload]);
  return (
    <section className="panel pm-list">
      <div className="pm-tools">
        <label className="search">
          <Icon name="search" size={16} />
          <input className="input" type="search" value={q} placeholder={t('pm.search')} aria-label={t('pm.search')} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="hd-filters" role="group" aria-label={t('pm.show')}>
          {FILTERS.map((f) => (
            <button key={f} className={`hd-chip${filter === f ? ' on' : ''}`} aria-pressed={filter === f} onClick={() => setParams(f === 'all' ? {} : { f }, { replace: true })}>
              {t(`pm.f_${f}` as MessageKey)}
            </button>
          ))}
        </div>
      </div>
      {!rows ? (
        <Skeleton h={240} />
      ) : !rows.length && filter === 'all' && !query ? (
        <EmptyState
          icon="card"
          title={t('pm.empty_title')}
          body={t('pm.empty_body')}
          action={ready && can('payments.links') ? <Button icon="plus" onClick={onNew}>{t('pm.new_link')}</Button> : undefined}
        />
      ) : (
        <Table
          head={[t('pm.col_customer'), t('pm.col_for'), t('pm.col_when'), t('pm.col_state'), t('pm.col_amount')]}
          left={[1, 2, 3]}
          phoneHide={[1, 2]}
          empty={t('pm.none_here')}
          rows={rows.map((r) => ({
            key: r.id,
            onClick: () => navigate(`/payments/${r.id}`),
            cells: [
              <span className="pm-who" key="who">
                <strong>{r.name || r.email || t('pm.no_name')}</strong>
                <span className="muted small">{r.description}</span>
              </span>,
              forLabel(r),
              dateTime(r.paid_at ?? r.at),
              <span className="pm-state" key="st">
                <StatusBadge tone={STATE[r.state].tone} icon={STATE[r.state].icon} label={stateLabel(r.state)} />
                {r.refunded > 0 ? <span className="muted small">{r.refunded >= r.amount ? t('pm.refunded_all') : t('pm.refunded_part')}</span> : null}
                {r.refund_pending ? <span className="muted small">{t('pm.refund_under_way')}</span> : null}
                {r.state === 'open' && r.error ? <span className="pm-err small">{t('pm.last_declined')}</span> : null}
              </span>,
              <span className="pm-amount" key="amt">
                {moneyIn(r.amount, r.currency)}
                {r.refunded > 0 ? <span className="muted small">−{moneyIn(r.refunded, r.currency)}</span> : null}
              </span>,
            ],
          }))}
        />
      )}
    </section>
  );
}

// ---- one payment ----------------------------------------------------------------------------

export function PaymentDetail() {
  const { id = '' } = useParams();
  const { can } = useSession();
  const { toast, fail } = useUi();
  const { data: d, reload } = useAction<Detail>('payments.get', { id });
  useEffect(() => subscribe(['payments'], (_e, p) => (!p?.intent || p.intent === id ? void reload() : undefined)), [reload, id]);
  const { busy, run } = useSave();
  const [cancel, setCancel] = useState(false);
  const [refund, setRefund] = useState(false);
  const copy = (s: string) => void navigator.clipboard?.writeText(s).then(() => toast(t('pm.copied')), fail);
  return (
    <Shell>
      <div className="page reports pm-page">
        <nav className="crumbs" aria-label={t('pm.crumbs')}>
          <Link to="/payments">{t('pm.title')}</Link>
          <Icon name="chevronRight" size={14} />
          <span>{d ? moneyIn(d.amount, d.currency) : '…'}</span>
        </nav>
        <div className="page-head">
          <div>
            <h1 className="pm-title">
              {d ? moneyIn(d.amount, d.currency) : ' '}
              {d ? <StatusBadge tone={STATE[d.state].tone} icon={STATE[d.state].icon} label={stateLabel(d.state)} /> : null}
            </h1>
            <p className="muted">{d ? [d.description, d.name].filter(Boolean).join(' · ') : ' '}</p>
          </div>
          {d ? (
            <span className="pm-actions">
              {d.state === 'open' || d.state === 'processing' ? (
                <Button icon="repeat" busy={busy} onClick={() => void run(() => action('payments.refresh', { id }).then(reload), t('pm.checked'))}>
                  {t('pm.check_now')}
                </Button>
              ) : null}
              {d.state === 'open' && can('payments.links') ? (
                <Button icon="x" onClick={() => setCancel(true)}>
                  {t('pm.cancel_link')}
                </Button>
              ) : null}
              {d.refundable > 0 && can('payments.refund') ? (
                <Button variant="primary" icon="undo" onClick={() => setRefund(true)}>
                  {t('pm.refund')}
                </Button>
              ) : null}
            </span>
          ) : null}
        </div>
        {!d ? (
          <Skeleton h={360} r="var(--radius-lg)" />
        ) : (
          <div className="pm-detail">
            <section className="panel">
              {d.test ? (
                <p className="callout callout-warn">
                  <Icon name="alert" size={16} /> {t('pm.test_payment')}
                </p>
              ) : null}
              {d.state === 'open' ? (
                <div className="pm-link">
                  <h2 className="pm-h2">{t('pm.link')}</h2>
                  <p className="muted small">{t('pm.link_hint', { date: dateTime(d.expires_at) })}</p>
                  <div className="pm-link-row">
                    <code>{fullUrl(d)}</code>
                    <Button size="sm" icon="copy" onClick={() => copy(fullUrl(d))}>
                      {t('pm.copy')}
                    </Button>
                    {d.email ? (
                      <a className="btn btn-secondary btn-sm" href={`mailto:${d.email}?subject=${encodeURIComponent(t('pm.mail_subject', { what: forLabel(d) }))}&body=${encodeURIComponent(t('pm.mail_body', { amount: moneyIn(d.amount, d.currency), what: d.description, link: fullUrl(d) }))}`}>
                        <Icon name="mail" size={16} />
                        <span className="btn-label">{t('pm.email_it')}</span>
                      </a>
                    ) : null}
                  </div>
                  {d.error ? (
                    <p className="pm-err small">
                      <Icon name="alert" size={14} /> {t('pm.last_try', { reason: d.error })}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <dl className="pm-facts">
                <dt>{t('pm.for')}</dt>
                <dd>{forLabel(d)}</dd>
                <dt>{t('pm.what')}</dt>
                <dd>{d.description}</dd>
                <dt>{t('pm.customer')}</dt>
                <dd>{[d.name, d.email].filter(Boolean).join(' · ') || '—'}</dd>
                <dt>{t('pm.account')}</dt>
                <dd>{d.account_name}</dd>
                <dt>{t('pm.made')}</dt>
                <dd>{[dateTime(d.at), d.created_by_name].filter(Boolean).join(' · ')}</dd>
                {d.paid_at ? (
                  <>
                    <dt>{t('pm.paid')}</dt>
                    <dd>{[dateTime(d.paid_at), d.method].filter(Boolean).join(' · ')}</dd>
                  </>
                ) : null}
                {d.state === 'cancelled' ? (
                  <>
                    <dt>{t('pm.cancelled_by')}</dt>
                    <dd>{d.closed_by_name ?? t('pm.by_system')}</dd>
                  </>
                ) : null}
                <dt>{t('pm.reference')}</dt>
                <dd className="pm-ref">{d.ref}</dd>
              </dl>
            </section>
            <section className="panel">
              <h2 className="pm-h2">{t('pm.refunds')}</h2>
              {d.refunds.length ? (
                <ul className="pm-refunds">
                  {d.refunds.map((r) => (
                    <li key={r.id}>
                      <span className="pm-who">
                        <strong>{moneyIn(r.amount, d.currency)}</strong>
                        <span className="muted small">{[r.reason, r.by_name ?? t('pm.by_system'), dateTime(r.at)].join(' · ')}</span>
                        {r.error ? <span className="pm-err small">{r.error}</span> : null}
                      </span>
                      <StatusBadge
                        tone={r.state === 'succeeded' ? 'ok' : r.state === 'failed' ? 'danger' : 'warn'}
                        icon={r.state === 'succeeded' ? 'check' : r.state === 'failed' ? 'alert' : 'clock'}
                        label={t(`pm.rf_${r.state}` as MessageKey)}
                      />
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted small">{d.state === 'succeeded' ? t('pm.no_refunds') : t('pm.refunds_after')}</p>
              )}
              {d.module === 'invoicing' && d.refunds.length ? <p className="muted small">{t('pm.invoice_refund_note')}</p> : null}
            </section>
          </div>
        )}
        {cancel && d ? (
          <Confirm
            title={t('pm.cancel_title')}
            body={t('pm.cancel_body')}
            confirm={t('pm.cancel_link')}
            danger
            busy={busy}
            onClose={() => setCancel(false)}
            onConfirm={() =>
              void run(async () => {
                await action('payments.cancel', { id });
                setCancel(false);
                await reload();
              }, t('pm.cancelled'))
            }
          />
        ) : null}
        {refund && d ? <RefundDialog d={d} onClose={() => setRefund(false)} onDone={reload} /> : null}
      </div>
    </Shell>
  );
}

function RefundDialog({ d, onClose, onDone }: { d: Detail; onClose: () => void; onDone: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const { toast } = useUi();
  const [amount, setAmount] = useState((d.refundable / 100).toFixed(2));
  const [reason, setReason] = useState('');
  const minor = parseMoney(amount);
  const tooMuch = minor !== null && minor > d.refundable;
  const save = () =>
    void run(async () => {
      const r = await action<RefundRow>('payments.refund', { id: d.id, amount: minor, reason });
      if (r.state === 'failed') toast(r.error ?? t('pm.refund_failed'), 'danger');
      else toast(r.state === 'succeeded' ? t('pm.refund_done') : t('pm.refund_sent'));
      onClose();
      await onDone();
    });
  return (
    <Dialog
      title={t('pm.refund_title')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="danger" icon="undo" busy={busy} disabled={!minor || minor <= 0 || tooMuch || reason.trim().length < 3} onClick={save}>
            {minor ? t('pm.refund_amount', { amount: moneyIn(minor, d.currency) }) : t('pm.refund')}
          </Button>
        </>
      }
    >
      <p className="muted">{t('pm.refund_lead', { method: d.method ?? t('pm.the_card') })}</p>
      <Field label={t('pm.amount')} hint={t('pm.refund_max', { amount: moneyIn(d.refundable, d.currency) })} error={tooMuch ? t('pm.refund_too_much') : null} required>
        <input className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Field>
      <Field label={t('pm.reason')} hint={t('pm.reason_hint')} required>
        <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
      </Field>
    </Dialog>
  );
}

/** A link for any amount, or for a record (an invoice) when `purpose` is given. */
export function NewLink({
  currency,
  purpose,
  onClose,
  onDone,
}: {
  currency: string;
  purpose?: { module: string; record: string; title: string; email?: string | null; name?: string | null };
  onClose: () => void;
  onDone?: () => Promise<unknown> | void;
}) {
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const [f, setF] = useState({ amount: '', description: '', name: purpose?.name ?? '', email: purpose?.email ?? '', days: '' });
  const [made, setMade] = useState<{ path: string; url: string | null; amount: number; currency: string; email: string | null; description: string } | null>(null);
  const minor = parseMoney(f.amount);
  const save = () =>
    void run(async () => {
      const out = await action<{ path: string; url: string | null; amount: number; currency: string; email: string | null; description: string }>('payments.create_link', {
        ...(purpose ? { purpose: { module: purpose.module, record: purpose.record } } : { amount: minor, description: f.description }),
        name: f.name || null,
        email: f.email || null,
        ...(f.days ? { days: Number(f.days) } : {}),
      });
      setMade(out);
      await onDone?.();
    });
  const copy = (s: string) => void navigator.clipboard?.writeText(s).then(() => toast(t('pm.copied')), fail);
  if (made) {
    const url = fullUrl(made);
    return (
      <Dialog
        title={t('pm.link_ready')}
        onClose={onClose}
        size="sm"
        footer={
          <Button variant="primary" onClick={onClose}>
            {t('pm.done')}
          </Button>
        }
      >
        <p className="pm-made">
          <Icon name="checkCircle" size={20} />
          <span>{t('pm.link_ready_body', { amount: moneyIn(made.amount, made.currency) })}</span>
        </p>
        <div className="pm-link-row">
          <code>{url}</code>
          <Button size="sm" icon="copy" onClick={() => copy(url)} data-autofocus>
            {t('pm.copy')}
          </Button>
        </div>
        {made.email ? (
          <a
            className="btn btn-secondary btn-md"
            href={`mailto:${made.email}?subject=${encodeURIComponent(t('pm.mail_subject', { what: made.description }))}&body=${encodeURIComponent(t('pm.mail_body', { amount: moneyIn(made.amount, made.currency), what: made.description, link: url }))}`}
          >
            <Icon name="mail" size={18} />
            <span className="btn-label">{t('pm.email_it')}</span>
          </a>
        ) : null}
      </Dialog>
    );
  }
  const ok = purpose ? true : !!minor && minor > 0 && f.description.trim().length > 0;
  return (
    <Dialog
      title={purpose ? t('pm.link_for', { what: purpose.title }) : t('pm.new_link')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="link" busy={busy} disabled={!ok} onClick={save}>
            {t('pm.make_link')}
          </Button>
        </>
      }
    >
      {purpose ? (
        <p className="muted">{t('pm.purpose_lead')}</p>
      ) : (
        <>
          <Field label={t('pm.amount')} hint={t('pm.amount_in', { currency })} required>
            <input className="input" inputMode="decimal" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-autofocus />
          </Field>
          <Field label={t('pm.what')} hint={t('pm.what_hint')} required>
            <input className="input" value={f.description} maxLength={200} onChange={(e) => setF({ ...f, description: e.target.value })} />
          </Field>
        </>
      )}
      <div className="pm-two">
        <Field label={t('pm.customer_name')}>
          <input className="input" value={f.name} maxLength={80} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </Field>
        <Field label={t('pm.customer_email')} hint={t('pm.email_hint')}>
          <input className="input" type="email" value={f.email} maxLength={120} onChange={(e) => setF({ ...f, email: e.target.value })} />
        </Field>
      </div>
      <Field label={t('pm.days')} hint={t('pm.days_hint')}>
        <input className="input" inputMode="numeric" value={f.days} placeholder="7" onChange={(e) => setF({ ...f, days: e.target.value.replace(/\D/g, '').slice(0, 2) })} />
      </Field>
    </Dialog>
  );
}

// ---- settings -------------------------------------------------------------------------------

const PROBLEM: Record<string, MessageKey> = {
  inactive: 'pm.pr_inactive',
  secret: 'pm.pr_secret',
  secret_unreadable: 'pm.pr_secret_unreadable',
  site_url: 'pm.pr_site_url',
  webhook: 'pm.pr_webhook',
};

function SettingsTab({ onChange }: { onChange: () => Promise<unknown> }) {
  const { data: s, reload } = useAction<SettingsData>('payments.settings');
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const [site, setSite] = useState<string | null>(null);
  const [days, setDays] = useState<string | null>(null);
  const [edit, setEdit] = useState<Account | 'stripe' | 'simulator' | null>(null);
  const [remove, setRemove] = useState<Account | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  const changed = async () => {
    await reload();
    await onChange();
  };
  if (!s) return <Skeleton h={320} />;
  const siteValue = site ?? s.site_url ?? '';
  const daysValue = days ?? String(s.link_days);
  const copy = (x: string) => void navigator.clipboard?.writeText(x).then(() => toast(t('pm.copied')), fail);
  const check = async (a: Account) => {
    setChecking(a.id);
    try {
      const r = await action<{ ok: boolean; live?: boolean; name?: string; error?: string }>('payments.check_account', { id: a.id });
      if (r.ok) toast(t(r.live ? 'pm.check_ok_live' : 'pm.check_ok_test', { name: r.name ?? a.name }));
      else toast(r.error ?? t('error.generic'), 'danger');
      await changed();
    } catch (err) {
      fail(err);
    }
    setChecking(null);
  };
  return (
    <div className="pm-settings">
      {!s.can_seal ? (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> {t('pm.no_seal')}
        </p>
      ) : null}
      <section className="panel">
        <div className="pm-panel-head">
          <div>
            <h2 className="pm-h2">{t('pm.providers')}</h2>
            <p className="muted small">{t('pm.providers_lead')}</p>
          </div>
          <span className="pm-actions">
            <Button icon="plus" variant="primary" onClick={() => setEdit('stripe')}>
              {t('pm.add_stripe')}
            </Button>
            <Button icon="plus" onClick={() => setEdit('simulator')}>
              {t('pm.add_simulator')}
            </Button>
          </span>
        </div>
        {!s.accounts.length ? (
          <EmptyState icon="card" title={t('pm.no_accounts')} body={t('pm.no_accounts_body')} />
        ) : (
          <ul className="pm-accounts">
            {s.accounts.map((a) => (
              <li key={a.id} className={`pm-acc${a.active ? '' : ' off'}`}>
                <div className="pm-acc-head">
                  <span className="pm-acc-icon" aria-hidden="true">
                    <Icon name={a.provider === 'stripe' ? 'card' : 'cpu'} size={18} />
                  </span>
                  <span className="pm-who">
                    <strong>
                      {a.name}
                      {a.is_default ? <span className="pm-default">{t('pm.default')}</span> : null}
                    </strong>
                    <span className="muted small">
                      {[t(`pm.pv_${a.provider}` as MessageKey), a.checked_name, a.checked_at ? t('pm.checked_at', { date: dateTime(a.checked_at) }) : null].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {!a.active ? (
                    <StatusBadge tone="neutral" icon="pause" label={t('pm.off')} />
                  ) : a.provider === 'simulator' || !a.live ? (
                    <StatusBadge tone="warn" icon="alert" label={t('pm.test')} />
                  ) : (
                    <StatusBadge tone="ok" icon="check" label={t('pm.live')} />
                  )}
                </div>
                {a.problems.length || a.last_error ? (
                  <ul className="pm-problems">
                    {a.last_error ? (
                      <li className="pm-err">
                        <Icon name="alert" size={14} /> {t('pm.last_error', { error: a.last_error })}
                      </li>
                    ) : null}
                    {a.problems.map((p) => (
                      <li key={p} className={p === 'webhook' ? 'muted' : 'pm-err'}>
                        <Icon name={p === 'webhook' ? 'info' : 'alert'} size={14} /> {t(PROBLEM[p] ?? 'pm.pr_other')}
                      </li>
                    ))}
                  </ul>
                ) : null}
                {a.webhook_url ? (
                  <div className="pm-hook">
                    <span className="small muted">{t('pm.webhook_address')}</span>
                    <div className="pm-link-row">
                      <code>{a.webhook_url}</code>
                      <Button size="sm" icon="copy" onClick={() => copy(a.webhook_url!)}>
                        {t('pm.copy')}
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div className="pm-actions">
                  <Button size="sm" icon="edit" onClick={() => setEdit(a)}>
                    {t('pm.edit')}
                  </Button>
                  {a.provider !== 'simulator' ? (
                    <Button size="sm" icon="repeat" busy={checking === a.id} disabled={!a.has_secret} onClick={() => void check(a)}>
                      {t('pm.check_connection')}
                    </Button>
                  ) : null}
                  {a.payments === 0 ? (
                    <Button size="sm" variant="ghost" icon="trash" onClick={() => setRemove(a)}>
                      {t('pm.remove')}
                    </Button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
      <ReadersPanel />
      <section className="panel">
        <h2 className="pm-h2">{t('pm.links')}</h2>
        <div className="pm-two">
          <Field label={t('pm.site_url')} hint={t('pm.site_url_hint')}>
            <input className="input" type="url" value={siteValue} placeholder="https://erp.example.com" onChange={(e) => setSite(e.target.value)} />
          </Field>
          <Field label={t('pm.link_days')} hint={t('pm.link_days_hint')}>
            <input className="input" inputMode="numeric" value={daysValue} onChange={(e) => setDays(e.target.value.replace(/\D/g, '').slice(0, 2))} />
          </Field>
        </div>
        <Button
          variant="primary"
          icon="check"
          busy={busy}
          disabled={site === null && days === null}
          onClick={() =>
            void run(async () => {
              await action('payments.save_settings', { site_url: siteValue.trim() || null, link_days: Number(daysValue) || 7 });
              setSite(null);
              setDays(null);
              await changed();
            }, t('pm.saved'))
          }
        >
          {t('pm.save')}
        </Button>
      </section>
      {edit ? <AccountDialog account={typeof edit === 'string' ? null : edit} provider={typeof edit === 'string' ? edit : edit.provider} canSeal={s.can_seal} hasSite={!!s.site_url} onClose={() => setEdit(null)} onSaved={changed} /> : null}
      {remove ? (
        <Confirm
          title={t('pm.remove_title', { name: remove.name })}
          body={t('pm.remove_body')}
          confirm={t('pm.remove')}
          danger
          busy={busy}
          onClose={() => setRemove(null)}
          onConfirm={() =>
            void run(async () => {
              await action('payments.delete_account', { id: remove.id });
              setRemove(null);
              await changed();
            }, t('pm.removed'))
          }
        />
      ) : null}
    </div>
  );
}

function AccountDialog({
  account,
  provider,
  canSeal,
  hasSite,
  onClose,
  onSaved,
}: {
  account: Account | null;
  provider: 'stripe' | 'simulator';
  canSeal: boolean;
  hasSite: boolean;
  onClose: () => void;
  onSaved: () => Promise<unknown>;
}) {
  const { busy, run } = useSave();
  const [f, setF] = useState({
    name: account?.name ?? (provider === 'stripe' ? 'Stripe' : t('pm.simulator_name')),
    active: account?.active ?? true,
    is_default: account?.is_default ?? true,
    secret: '',
    webhook: '',
  });
  const stripe = provider === 'stripe';
  const keyOk = !f.secret || /^(sk|rk)_(test|live)_[A-Za-z0-9]+$/.test(f.secret.trim());
  const hookOk = !f.webhook || /^whsec_[A-Za-z0-9+/=]+$/.test(f.webhook.trim());
  const save = () =>
    void run(async () => {
      await action('payments.save_account', {
        ...(account ? { id: account.id } : {}),
        name: f.name,
        provider,
        active: f.active,
        is_default: f.is_default,
        ...(f.secret.trim() ? { secret: f.secret.trim() } : {}),
        ...(f.webhook.trim() ? { webhook_secret: f.webhook.trim() } : {}),
      });
      onClose();
      await onSaved();
    }, t('pm.saved'));
  return (
    <Dialog
      title={account ? t('pm.edit_account', { name: account.name }) : stripe ? t('pm.add_stripe') : t('pm.add_simulator')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!f.name.trim() || !keyOk || !hookOk || (stripe && !account && !f.secret.trim())} onClick={save}>
            {t('pm.save')}
          </Button>
        </>
      }
    >
      {stripe ? (
        <ol className="pm-steps">
          <li>{t('pm.stripe_step1')}</li>
          <li>{t('pm.stripe_step2')}</li>
          <li>{hasSite ? t('pm.stripe_step3') : t('pm.stripe_step3_site')}</li>
        </ol>
      ) : (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> {t('pm.simulator_lead')}
        </p>
      )}
      <Field label={t('pm.account_name')} required>
        <input className="input" value={f.name} maxLength={60} onChange={(e) => setF({ ...f, name: e.target.value })} />
      </Field>
      {stripe ? (
        <>
          {!canSeal ? (
            <p className="callout callout-warn">
              <Icon name="alert" size={16} /> {t('pm.no_seal')}
            </p>
          ) : null}
          <Field
            label={t('pm.secret_key')}
            hint={account?.has_secret ? t('pm.secret_kept') : t('pm.secret_hint')}
            error={keyOk ? null : t('pm.secret_bad')}
            required={!account}
          >
            <input className="input" type="password" autoComplete="off" spellCheck={false} value={f.secret} placeholder={account?.has_secret ? '••••••••' : 'sk_live_…'} onChange={(e) => setF({ ...f, secret: e.target.value })} />
          </Field>
          <Field label={t('pm.webhook_secret')} hint={account?.has_webhook ? t('pm.secret_kept') : t('pm.webhook_hint')} error={hookOk ? null : t('pm.webhook_bad')}>
            <input className="input" type="password" autoComplete="off" spellCheck={false} value={f.webhook} placeholder={account?.has_webhook ? '••••••••' : 'whsec_…'} onChange={(e) => setF({ ...f, webhook: e.target.value })} />
          </Field>
          {account?.webhook_url ? (
            <p className="muted small">
              {t('pm.webhook_events')} <code>checkout.session.completed</code>, <code>checkout.session.async_payment_succeeded</code>, <code>checkout.session.async_payment_failed</code>,{' '}
              <code>checkout.session.expired</code>, <code>refund.updated</code>
            </p>
          ) : null}
        </>
      ) : null}
      <div className="pm-switches">
        <label className="pm-switch">
          <Switch checked={f.is_default} onChange={(v) => setF({ ...f, is_default: v })} label={t('pm.use_default')} />
          <span>{t('pm.use_default')}</span>
        </label>
        <label className="pm-switch">
          <Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={t('pm.active')} />
          <span>{t('pm.active')}</span>
        </label>
      </div>
    </Dialog>
  );
}

interface ReaderRow {
  id: string;
  label: string;
  account: string;
  account_name: string;
  provider: string;
  device_type: string | null;
  serial: string | null;
  place_name: string | null;
  active: boolean;
  online: boolean;
  simulated: boolean;
  test: boolean;
  checked_at: string | null;
  last_error: string | null;
  paid_30d: number;
}
interface ReaderSetup {
  readers: ReaderRow[];
  accounts: { id: string; name: string; provider: string; test: boolean }[];
}

/** The card readers at the counters: added with the code the reader shows, checked and removed here. */
function ReadersPanel() {
  const { data: s, reload } = useAction<ReaderSetup>('payments.reader_setup');
  const { busy, run } = useSave();
  const { toast, fail } = useUi();
  const [adding, setAdding] = useState(false);
  const [edit, setEdit] = useState<ReaderRow | null>(null);
  const [remove, setRemove] = useState<ReaderRow | null>(null);
  const [checking, setChecking] = useState<string | null>(null);
  if (!s) return <Skeleton h={160} />;
  const check = async (r: ReaderRow) => {
    setChecking(r.id);
    try {
      const out = await action<{ ok: boolean; online?: boolean; error?: string }>('payments.reader_check', { id: r.id });
      if (!out.ok) toast(out.error ?? t('error.generic'), 'danger');
      else toast(out.online ? t('rd.check_online', { name: r.label }) : t('rd.check_offline', { name: r.label }), out.online ? undefined : 'danger');
      await reload();
    } catch (err) {
      fail(err);
    }
    setChecking(null);
  };
  return (
    <section className="panel">
      <div className="pm-panel-head">
        <div>
          <h2 className="pm-h2">{t('rd.readers')}</h2>
          <p className="muted small">{t('rd.readers_lead')}</p>
        </div>
        {s.accounts.length ? (
          <span className="pm-actions">
            <Button icon="plus" onClick={() => setAdding(true)}>
              {t('rd.add')}
            </Button>
          </span>
        ) : null}
      </div>
      {!s.readers.length ? (
        <EmptyState icon="card" title={t('rd.none')} body={s.accounts.length ? t('rd.none_body') : t('rd.none_account')} />
      ) : (
        <ul className="pm-accounts">
          {s.readers.map((r) => (
            <li key={r.id} className={`pm-acc${r.active ? '' : ' off'}`}>
              <div className="pm-acc-head">
                <span className="pm-acc-icon" aria-hidden="true">
                  <Icon name="card" size={18} />
                </span>
                <span className="pm-who">
                  <strong>{r.label}</strong>
                  <span className="muted small">
                    {[r.place_name, r.device_type, r.account_name, r.paid_30d ? tn('rd.paid_30d', r.paid_30d) : null, r.checked_at ? t('pm.checked_at', { date: dateTime(r.checked_at) }) : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </span>
                <span className="rd-badges">
                  {r.test && r.active ? <StatusBadge tone="warn" icon="alert" label={t('pm.test')} /> : null}
                  {!r.active ? (
                    <StatusBadge tone="neutral" icon="pause" label={t('pm.off')} />
                  ) : r.online ? (
                    <StatusBadge tone="ok" icon="check" label={t('rd.online')} />
                  ) : (
                    <StatusBadge tone="danger" icon="wifiOff" label={t('rd.offline')} />
                  )}
                </span>
              </div>
              {r.last_error ? (
                <ul className="pm-problems">
                  <li className="pm-err">
                    <Icon name="alert" size={14} /> {t('pm.last_error', { error: r.last_error })}
                  </li>
                </ul>
              ) : null}
              <div className="pm-actions">
                <Button size="sm" icon="edit" onClick={() => setEdit(r)}>
                  {t('pm.edit')}
                </Button>
                {r.active ? (
                  <Button size="sm" icon="repeat" busy={checking === r.id} onClick={() => void check(r)}>
                    {t('rd.check')}
                  </Button>
                ) : null}
                {r.active ? (
                  <Button size="sm" variant="ghost" icon="trash" onClick={() => setRemove(r)}>
                    {t('pm.remove')}
                  </Button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
      {adding ? <AddReaderDialog accounts={s.accounts} onClose={() => setAdding(false)} onSaved={reload} /> : null}
      {edit ? <EditReaderDialog reader={edit} onClose={() => setEdit(null)} onSaved={reload} /> : null}
      {remove ? (
        <Confirm
          title={t('rd.remove_title', { name: remove.label })}
          body={remove.paid_30d ? t('rd.remove_body_used') : t('rd.remove_body')}
          confirm={t('pm.remove')}
          danger
          busy={busy}
          onClose={() => setRemove(null)}
          onConfirm={() =>
            void run(async () => {
              await action('payments.reader_remove', { id: remove.id });
              setRemove(null);
              await reload();
            }, t('rd.removed'))
          }
        />
      ) : null}
    </section>
  );
}

const NEW_PLACE = '__new';

function AddReaderDialog({ accounts, onClose, onSaved }: { accounts: ReaderSetup['accounts']; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const { fail } = useUi();
  const [f, setF] = useState({ account: accounts[0]!.id, label: '', code: '', place: '' });
  const [addr, setAddr] = useState({ name: '', line1: '', city: '', postal_code: '', country: '', state: '' });
  const [places, setPlaces] = useState<{ ref: string; name: string }[] | null>(null);
  const account = accounts.find((a) => a.id === f.account)!;
  useEffect(() => {
    setPlaces(null);
    action<{ ref: string; name: string }[]>('payments.reader_places', { account: f.account }).then(
      (list) => {
        setPlaces(list);
        setF((x) => ({ ...x, place: list[0]?.ref ?? NEW_PLACE }));
      },
      (err) => {
        setPlaces([]);
        setF((x) => ({ ...x, place: NEW_PLACE }));
        fail(err);
      },
    );
  }, [f.account, fail]);
  const isNew = f.place === NEW_PLACE;
  const addrOk = !isNew || (addr.name.trim() && addr.line1.trim() && addr.city.trim() && addr.postal_code.trim() && /^[A-Za-z]{2}$/.test(addr.country.trim()));
  const ok = f.label.trim() && f.code.trim().length >= 3 && f.place && addrOk && places;
  const save = () =>
    void run(async () => {
      await action('payments.reader_add', {
        account: f.account,
        label: f.label,
        code: f.code,
        ...(isNew ? { new_place: { ...addr, country: addr.country.trim().toUpperCase(), state: addr.state.trim() || null } } : { place: f.place }),
      });
      onClose();
      await onSaved();
    }, t('rd.added'));
  return (
    <Dialog
      title={t('rd.add')}
      onClose={onClose}
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!ok} onClick={save}>
            {t('rd.add_save')}
          </Button>
        </>
      }
    >
      {account.provider === 'stripe' ? (
        <ol className="pm-steps">
          <li>{t('rd.step1')}</li>
          <li>{t('rd.step2')}</li>
          {account.test ? <li>{t('rd.step_test')}</li> : null}
        </ol>
      ) : (
        <p className="callout callout-warn">
          <Icon name="alert" size={16} /> {t('rd.simulator_lead')}
        </p>
      )}
      {accounts.length > 1 ? (
        <Field label={t('rd.account')}>
          <select className="input" value={f.account} onChange={(e) => setF({ ...f, account: e.target.value })}>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </Field>
      ) : null}
      <div className="pm-two">
        <Field label={t('rd.label')} hint={t('rd.label_hint')} required>
          <input className="input" value={f.label} maxLength={60} onChange={(e) => setF({ ...f, label: e.target.value })} />
        </Field>
        <Field label={t('rd.code')} hint={t('rd.code_hint')} required>
          <input className="input" value={f.code} maxLength={60} autoComplete="off" spellCheck={false} onChange={(e) => setF({ ...f, code: e.target.value })} />
        </Field>
      </div>
      <Field label={t('rd.place')} hint={t('rd.place_hint')} required>
        <select className="input" value={f.place} disabled={!places} onChange={(e) => setF({ ...f, place: e.target.value })}>
          {(places ?? []).map((p) => (
            <option key={p.ref} value={p.ref}>
              {p.name}
            </option>
          ))}
          <option value={NEW_PLACE}>{t('rd.place_new')}</option>
        </select>
      </Field>
      {isNew ? (
        <fieldset className="rd-place">
          <legend className="small muted">{t('rd.place_address')}</legend>
          <Field label={t('rd.place_name')} required>
            <input className="input" value={addr.name} maxLength={80} onChange={(e) => setAddr({ ...addr, name: e.target.value })} />
          </Field>
          <Field label={t('rd.line1')} required>
            <input className="input" value={addr.line1} maxLength={120} autoComplete="address-line1" onChange={(e) => setAddr({ ...addr, line1: e.target.value })} />
          </Field>
          <div className="pm-two">
            <Field label={t('rd.city')} required>
              <input className="input" value={addr.city} maxLength={80} autoComplete="address-level2" onChange={(e) => setAddr({ ...addr, city: e.target.value })} />
            </Field>
            <Field label={t('rd.postal_code')} required>
              <input className="input" value={addr.postal_code} maxLength={20} autoComplete="postal-code" onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} />
            </Field>
            <Field label={t('rd.country')} hint={t('rd.country_hint')} required>
              <input className="input" value={addr.country} maxLength={2} autoComplete="country" onChange={(e) => setAddr({ ...addr, country: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })} />
            </Field>
            <Field label={t('rd.state')} hint={t('rd.state_hint')}>
              <input className="input" value={addr.state} maxLength={40} autoComplete="address-level1" onChange={(e) => setAddr({ ...addr, state: e.target.value })} />
            </Field>
          </div>
        </fieldset>
      ) : null}
    </Dialog>
  );
}

function EditReaderDialog({ reader, onClose, onSaved }: { reader: ReaderRow; onClose: () => void; onSaved: () => Promise<unknown> }) {
  const { busy, run } = useSave();
  const [f, setF] = useState({ label: reader.label, active: reader.active });
  return (
    <Dialog
      title={t('rd.edit', { name: reader.label })}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            icon="check"
            busy={busy}
            disabled={!f.label.trim()}
            onClick={() =>
              void run(async () => {
                await action('payments.reader_save', { id: reader.id, ...f });
                onClose();
                await onSaved();
              }, t('pm.saved'))
            }
          >
            {t('pm.save')}
          </Button>
        </>
      }
    >
      <Field label={t('rd.label')} required>
        <input className="input" value={f.label} maxLength={60} onChange={(e) => setF({ ...f, label: e.target.value })} />
      </Field>
      <label className="pm-switch">
        <Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={t('pm.active')} />
        <span>{t('pm.active')}</span>
      </label>
    </Dialog>
  );
}
